import React, { useState } from 'react';
import type { ConfigFieldDiff } from '../utils/nltConfigFieldDiff';
import {
  formatPatchSummaryLine,
  summarizePatchDiffs,
} from '../utils/nltPatchSummary';
import NltTextFieldDiffLines from './NltTextFieldDiffLines';
import './NLTPatchSummaryWidget.css';

interface NLTPatchSummaryWidgetProps {
  fieldDiffs: ConfigFieldDiff[];
  activeIndex: number;
  /** When true, clicking a change also scrolls to that field in the configurator. */
  canFocusConfigurator?: boolean;
  onIndexChange: (index: number) => void;
  onNavigate?: (path: string) => void;
}

const NLTPatchSummaryWidget: React.FC<NLTPatchSummaryWidgetProps> = ({
  fieldDiffs,
  activeIndex,
  canFocusConfigurator = false,
  onIndexChange,
  onNavigate,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (fieldDiffs.length === 0) return null;

  const stats = summarizePatchDiffs(fieldDiffs);
  const safeIndex = Math.min(Math.max(activeIndex, 0), fieldDiffs.length - 1);
  const current = fieldDiffs[safeIndex];
  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < fieldDiffs.length - 1;

  const goTo = (index: number) => {
    const nextIndex = Math.min(Math.max(index, 0), fieldDiffs.length - 1);
    onIndexChange(nextIndex);
    if (canFocusConfigurator) {
      onNavigate?.(fieldDiffs[nextIndex].path);
    }
  };

  return (
    <div className={`nlt-patch-summary${expanded ? ' nlt-patch-summary--expanded' : ''}`}>
      <div className="nlt-patch-summary__head">
        <button
          type="button"
          className="nlt-patch-summary__head-main"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <i
            className={`fas fa-chevron-${expanded ? 'down' : 'right'} nlt-patch-summary__chevron`}
            aria-hidden="true"
          />
          <span className="nlt-patch-summary__title">Patch</span>
          <span className="nlt-patch-summary__count">
            {stats.totalChanges} change{stats.totalChanges === 1 ? '' : 's'}
          </span>
        </button>
      </div>

      {!expanded ? (
        <p className="nlt-patch-summary__stats nlt-patch-summary__stats--compact">
          {formatPatchSummaryLine(stats)}
          {current ? ` · ${current.label}` : ''}
        </p>
      ) : (
        <>
          <p className="nlt-patch-summary__stats">{formatPatchSummaryLine(stats)}</p>
          <div className="nlt-patch-summary__nav">
            <button
              type="button"
              className="nlt-patch-summary__nav-btn"
              disabled={!hasPrev}
              onClick={() => goTo(safeIndex - 1)}
              aria-label="Previous change"
            >
              ‹
            </button>
            <span className="nlt-patch-summary__nav-pos" title={current?.label}>
              {safeIndex + 1}/{fieldDiffs.length}
            </span>
            <button
              type="button"
              className="nlt-patch-summary__nav-btn"
              disabled={!hasNext}
              onClick={() => goTo(safeIndex + 1)}
              aria-label="Next change"
            >
              ›
            </button>
          </div>
          {current ? (
            <div className="nlt-patch-summary__preview" aria-live="polite">
              <div className="nlt-patch-summary__preview-label">{current.label}</div>
              <div className="nlt-patch-summary__preview-body">
                <NltTextFieldDiffLines
                  oldValue={current.oldValue}
                  newValue={current.newValue}
                  mode="pending"
                  variant="summary"
                />
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default NLTPatchSummaryWidget;
